import Icon from '@mui/material/Icon'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from '../IntradayTradingView.module.less'
import type { IntradayPrices } from '../IntradayTradingView.types'
import { formatIntradayHourLabel } from '../utils/intradayHour.helper'

type IntradayPricesDisplayProps = {
  prices: IntradayPrices
  collapsible?: boolean
}

export function IntradayPricesDisplay({ prices, collapsible = true }: IntradayPricesDisplayProps): ReactNode {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  if (!prices.times || !prices.values || prices.times.length === 0) {
    return null
  }

  const table = (
    <div className={styles.IntradayTradingView_PriceTableWrapper}>
      <table className={styles.IntradayTradingView_PriceTable}>
        <thead>
          <tr>
            <th className={styles.IntradayTradingView_PriceTableTh}>{t('Hour')}</th>
            <th className={styles.IntradayTradingView_PriceTableTh}>{t('Price (SEK/MWh)')}</th>
          </tr>
        </thead>
        <tbody>
          {prices.times.map((time, index) => (
            <tr key={time} className={styles.IntradayTradingView_PriceTableRow}>
              <td className={styles.IntradayTradingView_PriceTableLabelCell}>{formatHourLabel(time)}</td>
              <td className={styles.IntradayTradingView_PriceTableValueCell}>
                {prices.values[index]?.toFixed(2) ?? '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  if (!collapsible) {
    return <div className={styles.IntradayTradingView_PricesInfo}>{table}</div>
  }

  return (
    <div className={`${styles.IntradayTradingView_PricesInfo} ${styles.IntradayTradingView_OneThirdWidth}`}>
      <details
        open={isExpanded}
        className={styles.IntradayTradingView_PricesDetails}
        onToggle={(event) => setIsExpanded(event.currentTarget.open)}
      >
        <summary className={styles.IntradayTradingView_PricesDetailsTitle}>
          <span>{t('Used input prices')}</span>
          <Icon className={isExpanded ? 'fal fa-chevron-up' : 'fal fa-chevron-down'} fontSize="small" />
        </summary>
        {table}
      </details>
    </div>
  )
}

function formatHourLabel(time: ISODateTime | number): string {
  if (typeof time === 'number') {
    return String(time)
  }

  try {
    return formatIntradayHourLabel(time)
  } catch {
    return String(time)
  }
}
